import type { PaymentRoutesTranslations } from "../interfaces/paymentRoutes";

export const paymentRoutesTranslations: PaymentRoutesTranslations = {
  // Página
  page_title: "Corredores e provedores",
  page_subtitle:
    "Quais corredores existem e com qual provedor e modelo econômico cada entrada e saída é executada. Um corredor sem rota recusa todas as suas operações.",
  refresh_button: "Atualizar",
  refresh_aria: "Recarregar os corredores",
  button_new_route: "Nova rota",

  // Estados de carregamento
  truncated_notice:
    "Foram carregadas {{loaded}} de {{total}} rotas. A cobertura pode estar incompleta até carregar todas.",
  error_load: "Não foi possível carregar o catálogo de corredores.",
  error_load_detail: "Nenhuma linha é exibida: o estado dos corredores nunca é inventado.",
  error_retry: "Tentar novamente",

  // Indicadores
  kpi_routed_label: "Corredores roteados",
  kpi_routed_meta: "de {{total}} corredores operacionais",
  kpi_missing_label: "Sem rota",
  kpi_missing_meta: "Recusam todas as entradas e saídas",
  kpi_missing_ok: "Todos os corredores têm rota",
  kpi_blocked_label: "Bloqueados",
  kpi_blocked_meta: "Rota inativa ocupando o corredor",
  kpi_blocked_ok: "Nenhuma rota inativa",
  kpi_unsupported_label: "Provedor sem capacidade",
  kpi_unsupported_meta: "As saídas ficarão paradas no despacho",
  kpi_unsupported_ok: "Todas as rotas têm provedor capaz",

  // Estados de um corredor
  status_routed: "Roteado",
  status_missing: "Sem rota",
  status_blocked: "Bloqueado",
  status_unsupported: "Sem cobertura",
  consequence_routed: "O corredor opera normalmente.",
  consequence_missing:
    "Não há rota configurada: todas as entradas e saídas deste corredor são recusadas antes de calcular preços.",
  consequence_blocked:
    "Há uma rota inativa ocupando o corredor: ela não opera e também não admite outra rota. Não é possível reativá-la — é preciso excluí-la e criar a nova.",
  consequence_unsupported:
    "O provedor configurado não cobre este corredor. As saídas reservam fundos e ficam paradas no despacho, aparecendo em Resgate de dispersões.",

  // Tabela
  th_corridor: "Corredor",
  th_direction: "Direção",
  th_status: "Estado",
  th_model: "Modelo econômico",
  th_provider: "Provedor",
  provider_corridors: "Roteia {{count}} corredor(es)",
  provider_unknown: "Provedor não reconhecido",
  provider_empty: "Sem provedor",
  offcatalog_badge: "Fora do catálogo",
  offcatalog_notice:
    "Estas rotas apontam para corredores que nenhum fluxo do dashboard alcança. São exibidas porque existem na configuração, não porque possam ser usadas.",

  // Ações
  action_view: "Ver",
  action_create: "Criar rota",
  action_switch: "Trocar de provedor",
  action_delete: "Excluir rota",

  // Vazio e contexto
  empty_title: "Nenhum corredor configurado",
  empty_description:
    "Sem rotas, o motor de pagamentos recusa todas as entradas e saídas. Crie a primeira rota para habilitar um corredor.",
  why_banner:
    "Cada rota define um corredor: sem ela não há operações e, se apontar para um provedor que não o cobre, as saídas ficam paradas com os fundos já reservados.",

  // Painel lateral
  drawer_subtitle: "Detalhe da rota configurada para este corredor.",
  drawer_section_corridor: "Corredor",
  drawer_section_provider: "Provedor",
  drawer_section_audit: "Registro",
  drawer_field_status: "Estado",
  drawer_field_model: "Modelo econômico",
  drawer_field_capability: "Capacidade para este corredor",
  drawer_field_id: "Identificador da rota",
  drawer_field_created: "Criada",
  drawer_field_updated: "Atualizada",
  capability_ok: "O provedor declara este corredor",
  capability_missing: "O provedor não declara este corredor",
  drawer_link_fees: "Ver tarifas deste corredor",
  drawer_copy_id: "Copiar identificador",
  drawer_copied: "Identificador copiado",

  // Diálogo de criação
  create_title: "Nova rota",
  create_subtitle: "Habilite um corredor escolhendo quem o executa e com qual modelo econômico.",
  create_section_corridor: "Corredor",
  create_section_provider: "Provedor",
  create_section_model: "Modelo econômico",
  create_corridor_label: "Corredor",
  create_corridor_placeholder: "Escolha um corredor",
  create_corridor_helper: "O trilho e a moeda vêm do corredor: o motor fixa um de cada por país.",
  create_direction_label: "Direção",
  direction_payin_description: "Dinheiro que entra: cobranças dos clientes.",
  direction_payout_description: "Dinheiro que sai: dispersões para beneficiários.",
  create_provider_label: "Quem executa este corredor",
  create_provider_helper:
    "Só são oferecidos os provedores que declaram este corredor. Um que não o cobre deixaria as saídas paradas.",
  provider_option_label: "Provedor do corredor",
  provider_option_coverage: "Cobre: {{corridors}}",
  provider_option_meta: "Já roteia {{count}} corredor(es)",
  provider_option_meta_none: "Ainda não roteia nenhum corredor",
  provider_pick_corridor_first:
    "Escolha primeiro o corredor e a direção para ver qual provedor pode executá-lo.",
  create_provider_none_title: "Nenhum provedor cobre este corredor",
  create_provider_none_description:
    "Não há provedor integrado capaz de executá-lo, então uma rota aqui só deixaria as operações paradas. É preciso integrá-lo antes de habilitar o corredor.",
  create_model_label: "Quem paga o custo do provedor",
  create_model_helper: "Define a fórmula do líquido e os lançamentos contábeis de cada operação do corredor.",
  create_summary_title: "O que vai acontecer",
  create_summary_sentence: "As operações de {{corridor}} serão executadas com {{model}}.",
  create_summary_pick_corridor: "Escolha um corredor e uma direção para ver o resultado.",
  create_warn_exists:
    "Este corredor já tem uma rota. O motor admite apenas uma por corredor: para reatribuí-lo, use «Trocar de provedor».",
  create_warn_blocked:
    "Este corredor tem uma rota inativa que o mantém ocupado. É preciso excluí-la antes de criar a nova.",
  create_next_step:
    "A rota fica ativa imediatamente. Verifique se o corredor tem tarifas configuradas: sem elas ele também recusa operações.",
  submit: "Criar rota",
  cancel: "Cancelar",
  validation_corridor_required: "Escolha o corredor e a direção.",
  validation_provider_required: "Escolha o provedor que vai executar o corredor.",

  // Diálogo de exclusão
  delete_title: "Excluir a rota do corredor",
  delete_semantics: "A rota é removida e o corredor fica livre para configurar outra.",
  delete_consequence:
    "A partir desse momento, {{corridor}} recusa todas as suas operações até existir uma rota nova.",
  delete_consequence_blocked:
    "A rota está inativa, então {{corridor}} já recusa suas operações: excluí-la não piora nada e é a única forma de liberar o corredor.",
  delete_frees_corridor: "Excluí-la é o passo obrigatório para apontar o corredor para outro provedor.",
  delete_confirm: "Excluir rota",
  delete_cancel: "Cancelar",

  // Diálogo de troca de provedor
  switch_title: "Trocar de provedor",
  switch_subtitle: "O motor admite uma única rota por corredor, então a troca exclui a atual e cria a nova.",
  switch_current_label: "Rota atual",
  switch_gap_warning:
    "Entre excluir a rota atual e criar a nova, o corredor fica alguns instantes sem rota: as operações que caírem nessa janela são recusadas.",
  switch_no_alternative:
    "Não há outro provedor integrado que cubra este corredor, então não há para onde trocá-lo.",
  switch_confirm: "Trocar provedor",
  switch_failed_title: "O corredor ficou sem rota",
  switch_failed_detail:
    "A rota anterior foi excluída, mas a nova não pôde ser criada, então o corredor está recusando operações. Tente criar novamente.",
  switch_retry: "Tentar criar novamente",

  // Avisos
  toast_created: "Rota criada: {{corridor}} já opera.",
  toast_deleted: "Rota excluída: {{corridor}} fica sem rota.",
  toast_switched: "Provedor trocado: {{corridor}} já opera com o novo.",
  error_delete_missing: "A rota já não existia. A visão foi atualizada.",
  error_no_write_permission: "Você não tem permissão para alterar os corredores.",
  error_create: "Não foi possível criar a rota.",
  error_delete: "Não foi possível excluir a rota.",
};
